import * as _ from 'lodash';
import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import type { InjectedRouter } from 'react-router';

import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import { SidebarLayout } from '../../layouts';
import DependenciesHeader from './header';
import DependenciesTable from './DependenciesTable';
import DependencyFilters from './filters';
import type { IAsync, Project, RouterProps } from '../../../types';
import type { ProjectGroup, ProjectGroupRelease } from '../../../../shared/model-typings';
import { useSyncStateAndURLParams } from './hooks/useSyncStateAndURLParams';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import { ProjectSource } from '../../../../typings/enums';
import { useOnUnmount } from '../../../hooks';
import useDependencyModals from './hooks/useDependencyModals';
import { AddDependency, DeleteDependency, StreamBuild, ViewRelationships } from './modals';
import { window } from '../../../globals';
import styles from './dependencies.module.less';
import ModalEditDependency from '../../modals/ModalEditDependency';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';

const countOptions = [
  25,
  50,
  75,
  100,
];

export type DependenciesV2Props = {
  router: InjectedRouter;
  project?: IAsync<Project>;
  projectGroup?: ProjectGroup;
  projectGroupRelease?: ProjectGroupRelease;
  onProjectChange?: () => void;
} & RouterProps;

const DependenciesV2: VoidFunctionComponent<DependenciesV2Props> = ({
  router,
  location,
  params,
  project,
  projectGroup,
  projectGroupRelease,
  onProjectChange = _.noop,
}) => {
  const dispatch = useDispatch();
  useSyncStateAndURLParams({ router, location, params });
  const modals = useDependencyModals();

  const { project_id: projectId, revision_id: revisionId } = params;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);

  // optional projectRevision used by the EditDependency modal
  const projectRevision = scope && scope.scope === 'project' ? scope.revision : undefined;

  const isContainerProject = project?.data?.type === ProjectSource.Container;
  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);

  const page = useSelector(DependenciesV2Store.selectors.getPage);
  const count = useSelector(DependenciesV2Store.selectors.getCount);
  const totalItems = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const setPage = useFetchDependencyAction(DependenciesV2Store.actions.setPage);
  const setCount = useFetchDependencyAction(DependenciesV2Store.actions.setCount);

  // If the current user is authenticated, check if they belong to the current scope target's org.
  if (window.user) {
    // Check if the user is logged in and belongs to the project's org.
    if (project && window.user.organizationId === project.data?.organizationId) {
      dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
    }
    // Check if the user is logged in and belongs to the project group's org.
    if (projectGroup && window.user.organizationId === projectGroup.organizationId) {
      dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
    }
  }

  useEffect(() => {
    if (projectGroup && projectGroupRelease) {
      dispatch(DependenciesV2Store.actions.setScope({
        scope: 'releaseGroup',
        projectGroupId: projectGroup.id,
        projectGroupReleaseId: projectGroupRelease.id,
      }));
    } else if (projectId && revisionId) {
      const revision = `${projectId}$${revisionId}`;
      dispatch(DependenciesV2Store.actions.setScope({ scope: 'project', revision }));
    }
  }, [dispatch, projectId, revisionId, projectGroup, projectGroupRelease]);

  useOnUnmount(() => {
    dispatch(DependenciesV2Store.actions.resetState());
  });

  return (
    <div className={cx('container', 'container-responsive', styles.DependenciesContainer)}>
      <SidebarLayout>
        <SidebarLayout.Header>
          <DependenciesHeader depModals={modals} />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <DependenciesTable modals={modals} />
          <PaginationFooter
            page={page}
            count={count}
            totalItems={totalItems}
            setPage={setPage}
            setCount={setCount}
            countOptions={countOptions}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <DependencyFilters
            isContainerProject={isContainerProject}
            isReleaseGroupScope={isReleaseGroupScope}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      <StreamBuild modals={modals} />
      <ModalEditDependency
        show={modals.editDependencyModal.isOpen}
        onHide={modals.editDependencyModal.onClose}
        onChange={onProjectChange}
        revisionLocator={modals.editDependencyModal.context.revision}
        parentRevisionLocator={projectRevision}
      />
      {/* The following modals only apply to project dependencies. */}
      {scope && scope.scope === 'project' && (
        <>
          <ViewRelationships projectRevision={scope.revision} modals={modals} />
          <AddDependency projectRevision={scope.revision} modals={modals} onProjectChange={onProjectChange} />
          <DeleteDependency projectRevision={scope.revision} modals={modals} onProjectChange={onProjectChange} />
          <ModalManagePackageLabels
            show={modals.managePackageLabelsModal.isOpen}
            onHide={modals.managePackageLabelsModal.onClose}
            context={modals.managePackageLabelsModal.context}
            parentRevisionLocator={projectRevision}
            isGlobalEnabled
            isProjectEnabled
            isRevisionEnabled
          />
        </>
      )}
    </div>
  );
};

export default DependenciesV2;
